import { Injectable, NgZone, inject, isDevMode } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AppUpdateService {
    private swUpdate = inject(SwUpdate);
    private ngZone = inject(NgZone);
    private initialized = false;
    private readonly reloadMarkerKey = 'rc_sw_update_reloading';

    initialize(): void {
        if (this.initialized || isDevMode() || !this.swUpdate.isEnabled) {
            return;
        }

        this.initialized = true;
        this.clearReloadMarkerSoon();

        this.swUpdate.versionUpdates
            .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
            .subscribe(() => {
                void this.activateUpdateAndReload();
            });

        this.swUpdate.unrecoverable.subscribe(() => this.reloadOnce());

        this.ngZone.runOutsideAngular(() => {
            window.setTimeout(() => void this.checkForUpdate(), 3000);
            window.setInterval(() => void this.checkForUpdate(), 60 * 60 * 1000);
        });
    }

    private async checkForUpdate(): Promise<void> {
        try {
            await this.swUpdate.checkForUpdate();
        } catch {
            // Best effort: update checks must never block the app.
        }
    }

    private async activateUpdateAndReload(): Promise<void> {
        try {
            await this.swUpdate.activateUpdate();
        } catch {
            // If activation fails, reload still lets the browser ask for the newest manifest.
        }

        this.reloadOnce();
    }

    private reloadOnce(): void {
        try {
            if (sessionStorage.getItem(this.reloadMarkerKey) === '1') {
                return;
            }
            sessionStorage.setItem(this.reloadMarkerKey, '1');
        } catch {
            // sessionStorage can be unavailable in hardened browser contexts.
        }

        window.location.reload();
    }

    private clearReloadMarkerSoon(): void {
        try {
            if (sessionStorage.getItem(this.reloadMarkerKey) !== '1') {
                return;
            }

            window.setTimeout(() => {
                sessionStorage.removeItem(this.reloadMarkerKey);
            }, 10000);
        } catch {
            // No-op.
        }
    }
}
